import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Heart, Brain, Wind, Activity } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const NerfVaguePage = () => {

  const functions = [
    {
      icon: <Heart className="w-8 h-8 text-sky-500" />,
      title: "Régulation Cardiovasculaire",
      description: "Le nerf vague ralentit le rythme cardiaque, réduit la tension artérielle et améliore la variabilité de la fréquence cardiaque (VFC), un marqueur clé de santé."
    },
    {
      icon: <Wind className="w-8 h-8 text-sky-500" />,
      title: "Contrôle Respiratoire",
      description: "Il régule la fréquence et la profondeur de la respiration, permettant une respiration diaphragmatique calme et efficace."
    },
    {
      icon: <Activity className="w-8 h-8 text-slate-500" />,
      title: "Digestion et Métabolisme",
      description: "Stimule la production d'enzymes digestives, favorise le péristaltisme intestinal et régule la sécrétion d'insuline."
    },
    {
      icon: <Brain className="w-8 h-8 text-slate-500" />,
      title: "Inflammation et Immunité",
      description: "Active la 'voie anti-inflammatoire cholinergique', réduisant l'inflammation chronique et modulant la réponse immunitaire."
    }
  ];

  const techniques = [
    {
      title: "Respiration Diaphragmatique Lente",
      description: "Pratiquez 5-10 minutes de respiration lente (5-6 cycles/minute) avec une expiration prolongée. Cela stimule directement le nerf vague et active le système parasympathique.",
      emphasis: "Technique #1 la plus efficace"
    },
    {
      title: "Cohérence Cardiaque",
      description: "Respirez à un rythme de 6 cycles par minute (5 secondes d'inspiration, 5 secondes d'expiration) pendant 5 minutes, 3 fois par jour. Cette pratique maximise la variabilité de la fréquence cardiaque."
    },
    {
      title: "Chant, Bourdonnement (Humming) et Gargarisme",
      description: "Ces activités créent des vibrations qui stimulent mécaniquement le nerf vague dans la gorge. Pratiquez 2-3 minutes de bourdonnement quotidien."
    },
    {
      title: "Exposition au Froid",
      description: "Douches froides (30 secondes à 2 minutes), immersion du visage dans l'eau froide. Le froid active le 'diving reflex' qui stimule puissamment le nerf vague."
    },
    {
      title: "Méditation et Pleine Conscience",
      description: "La méditation régulière augmente le tonus vagal. Même 10 minutes par jour peuvent avoir des effets mesurables sur la variabilité cardiaque."
    },
    {
      title: "Massage et Stimulation Physique",
      description: "Massage doux du cou, de la nuque et derrière les oreilles. Pression légère sur le sinus carotidien (sous supervision médicale)."
    },
    {
      title: "Yoga et Étirements",
      description: "Postures inversées, flexions avant et torsions douces stimulent le nerf vague. Les postures de relaxation profonde sont particulièrement efficaces."
    },
    {
      title: "Rire et Connexions Sociales",
      description: "Le rire authentique et les interactions sociales positives augmentent l'activité vagale et réduisent le stress."
    }
  ];

  // Stanley Rosenberg's Basic Exercise
  const rosenbergExercise = {
    title: "L'Exercice de Base de Stanley Rosenberg",
    subtitle: "Une technique rapide et très efficace issue de la théorie polyvagale",
    objective: "Apaiser le système nerveux, réduire le stress et l'anxiété, améliorer la digestion, le sommeil et la régulation émotionnelle, sortir d'un état de vigilance excessive (sympathique) ou de figement (dorsal).",
    steps: [
      {
        title: "Position",
        instructions: [
          "Allongez-vous sur le dos, sur une surface confortable",
          "Bras le long du corps",
          "Jambes détendues",
          "La tête repose droite, regard vers le plafond"
        ]
      },
      {
        title: "Placement des mains",
        instructions: [
          "Entrelacez les doigts et placez les mains derrière la tête, à la base du crâne (occiput)",
          "Les coudes restent ouverts et détendus"
        ]
      },
      {
        title: "Mouvement des yeux",
        instructions: [
          "Sans bouger la tête, regardez lentement vers la droite uniquement avec les yeux",
          "Allez jusqu'à la limite confortable, sans forcer"
        ]
      },
      {
        title: "Maintien",
        instructions: [
          "Restez dans cette position 30 secondes à 1 minute",
          "Attendez un signe de relâchement : soupir, bâillement, déglutition, respiration plus profonde, sensation de calme"
        ]
      },
      {
        title: "Retour au centre",
        instructions: [
          "Revenez doucement au regard central",
          "Observez les sensations quelques instants"
        ]
      },
      {
        title: "Autre côté",
        instructions: [
          "Répétez exactement la même chose en regardant vers la gauche"
        ]
      }
    ],
    signs: [
      "Sensation de détente dans la poitrine ou le ventre",
      "Respiration plus lente",
      "Chaleur, lourdeur agréable",
      "Apaisement émotionnel"
    ],
    tips: [
      "Ne forcez jamais le regard",
      "Si une émotion monte, laissez-la passer sans l'analyser",
      "Pratiquez 1 à 2 fois par jour, ou en cas de stress",
      "L'exercice doit rester doux et sécurisant"
    ],
    reference: {
      author: "Stanley Rosenberg",
      title: "Accessing the Healing Power of the Vagus Nerve: Self-Help Exercises for Anxiety, Depression, Trauma, and Autism",
      preface: "Stephen W. Porges",
      year: "2017",
      publisher: "North Atlantic Books, Berkeley"
    }
  };

  const benefits = [
    "Réduction du stress et de l'anxiété",
    "Amélioration du sommeil",
    "Meilleure digestion",
    "Réduction de l'inflammation chronique",
    "Régulation de l'humeur",
    "Amélioration de la concentration",
    "Renforcement du système immunitaire",
    "Meilleure récupération après l'effort"
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF', fontFamily: 'Figtree, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Système nerveux"
                title="Le Nerf"
                highlight="Vague"
                subtitle="Le super-highway de votre système nerveux parasympathique et la clé de votre bien-être."
                align="left"
              />
            </div>
            <div className="flex justify-center">
              <img 
                src="/nerf-vague-hero.png"
                alt="Nerf Vague - Illustration anatomique"
                className="w-72 h-72 lg:w-96 lg:h-96 object-cover rounded-full shadow-2xl"
                style={{ border: '4px solid #E0F2FE' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <div className="space-y-6" style={{ color: '#475569' }}>
            <p className="text-lg leading-relaxed">
              Le nerf vague (du latin <em>vagus</em>, qui signifie "errant") est le <strong style={{ color: '#0F172A' }}>10ème nerf crânien</strong> et 
              le nerf le plus long et le plus complexe du système nerveux autonome. Il relie directement le cerveau à 
              la majorité de nos organes vitaux, jouant un rôle crucial dans la régulation de presque toutes les 
              fonctions corporelles automatiques.
            </p>
            <p className="text-lg leading-relaxed mb-4">
              Souvent appelé le <strong>"nerf de la compassion"</strong> ou le <strong>"nerf de la récupération"</strong>, 
              il est le principal représentant du système nerveux parasympathique - notre système de "repos et digestion" 
              qui s'oppose au système sympathique du "combat ou fuite".
            </p>
          </div>
        </div>
      </section>

      {/* Anatomical Image */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Anatomie du Nerf Vague
          </h2>

          <Card className="border-0 shadow-xl overflow-hidden">
            <CardContent className="p-8">
              <div className="flex flex-col lg:flex-row gap-8 items-center">
                <div className="lg:w-1/2">
                  <img 
                    src="/nerf-vague-anatomie.png"
                    alt="Anatomie du nerf vague - Illustration complète"
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Illustration anatomique du nerf vague et ses ramifications vers les organes vitaux
                  </p>
                </div>
                
                <div className="lg:w-1/2 space-y-4">
                  <h3 className="text-xl font-medium mb-4">Parcours Anatomique</h3>
                  <div className="space-y-3 text-slate-600">
                    <p><strong>Origine :</strong> Noyau du nerf vague dans le bulbe rachidien (tronc cérébral)</p>
                    <p><strong>Sortie du crâne :</strong> Par le foramen jugulaire</p>
                    <p><strong>Trajet descendant :</strong></p>
                    <ul className="ml-6 space-y-2">
                      <li>• Cou : le long de l'artère carotide</li>
                      <li>• Thorax : ramifications vers le cœur et les poumons</li>
                      <li>• Abdomen : innerve l'estomac, le foie, le pancréas, les intestins</li>
                    </ul>
                    <p className="mt-4 p-4 bg-sky-50 rounded-lg border border-indigo-200">
                      <strong>Fait remarquable :</strong> 80% des fibres du nerf vague sont <em>afférentes</em> 
                      (remontent vers le cerveau), transmettant constamment des informations sur l'état de vos organes. 
                      Seulement 20% sont efférentes (descendent du cerveau).
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Functions */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Fonctions Principales
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Le nerf vague influence pratiquement tous les aspects de votre santé
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {functions.map((func, index) => (
              <Card key={index} className="border-2 border-slate-100 hover:border-indigo-200 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 p-3 rounded-lg">
                      {func.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{func.title}</h3>
                      <p className="text-slate-600">{func.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
            <h4 className="font-medium mb-3"> Le Nerf Vague et le Cerveau</h4>
            <p className="text-slate-800">
              Le nerf vague joue un rôle crucial dans l'<strong>axe intestin-cerveau</strong>. Il transmet 
              des signaux du microbiome intestinal vers le cerveau, influençant l'humeur, l'anxiété et même 
              les fonctions cognitives. C'est pourquoi on appelle l'intestin notre "deuxième cerveau".
            </p>
          </div>
        </div>
      </section>

      {/* Importance of Vagal Tone */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            L'Importance du Tonus Vagal
          </h2>
          
          <div className="prose prose-lg max-w-none text-slate-600 mb-8">
            <p className="text-lg leading-relaxed mb-4">
              Le <strong>tonus vagal</strong> fait référence à l'activité du nerf vague. Un tonus vagal élevé est 
              associé à une meilleure santé globale, tandis qu'un faible tonus vagal est lié à de nombreux problèmes 
              de santé chroniques.
            </p>
            <p className="text-lg leading-relaxed mb-4">
              Le tonus vagal peut être mesuré par la <strong>variabilité de la fréquence cardiaque (VFC)</strong> - 
              la variation du temps entre chaque battement de cœur. Une VFC élevée indique un bon tonus vagal et 
              une capacité d'adaptation optimale au stress.
            </p>
          </div>

          <Card className="border-0 shadow-xl mb-8">
            <CardContent className="p-8">
              <h3 className="text-xl font-medium mb-6">Bénéfices d'un Tonus Vagal Élevé</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-sky-500 font-bold mt-1">✓</span>
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="p-6 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
            <h4 className="font-bold text-sky-900 mb-3"> Conséquences d'un Faible Tonus Vagal</h4>
            <div className="text-sky-800 space-y-2">
              <p>Un tonus vagal faible est associé à :</p>
              <ul className="ml-6 space-y-1">
                <li>• Anxiété chronique et dépression</li>
                <li>• Inflammation systémique</li>
                <li>• Maladies cardiovasculaires</li>
                <li>• Troubles digestifs (IBS, reflux)</li>
                <li>• Fatigue chronique</li>
                <li>• Difficultés de récupération après le stress</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Techniques */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Techniques pour Stimuler le Nerf Vague
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-3xl mx-auto">
            Rééquilibrez votre système nerveux autonome et améliorez votre tonus vagal avec ces pratiques validées scientifiquement
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {techniques.map((technique, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </span>
                    <h3 className="text-lg font-medium">{technique.title}</h3>
                  </div>
                  <p className="text-slate-600 ml-11">{technique.description}</p>
                  {technique.emphasis && (
                    <div className="ml-11 mt-3 px-3 py-2 border border-sky-200 rounded text-sm text-slate-900 font-semibold">
                       {technique.emphasis}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 p-8 bg-white/60 backdrop-blur-md rounded-2xl border border-white/70 shadow-lg">
            <h3 className="text-2xl font-medium mb-4 text-slate-900"> Programme Pratique Quotidien</h3>
            <div className="space-y-3 text-slate-800">
              <p><strong>Matin :</strong> 5 minutes de cohérence cardiaque + 2 minutes de bourdonnement</p>
              <p><strong>Midi :</strong> 5 minutes de cohérence cardiaque</p>
              <p><strong>Soir :</strong> 5 minutes de cohérence cardiaque + 10 minutes de méditation ou yoga</p>
              <p className="mt-4 pt-4 border-t border-white/50 text-sm">
                <strong>Conseil :</strong> Commencez progressivement. Même 5 minutes de respiration diaphragmatique 
                lente par jour peuvent améliorer significativement votre tonus vagal en quelques semaines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stanley Rosenberg's Basic Exercise - Special Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1 bg-sky-100 text-sky-600 rounded-full text-sm font-medium mb-4">
               Technique Recommandée
            </span>
            <h2 className="text-3xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {rosenbergExercise.title}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {rosenbergExercise.subtitle}
            </p>
          </div>

          {/* Objective */}
          <Card className="border-0 shadow-lg mb-8">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-sky-600 mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Objectif
              </h3>
              <p className="text-slate-600">{rosenbergExercise.objective}</p>
            </CardContent>
          </Card>

          {/* Steps */}
          <Card className="border-0 shadow-xl mb-8">
            <CardContent className="p-8">
              <h3 className="text-xl font-medium mb-6 flex items-center gap-2">
                📋 Description de l'exercice
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {rosenbergExercise.steps.map((step, index) => (
                  <div key={index} className="p-5 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </span>
                      <h4 className="font-medium">{step.title}</h4>
                    </div>
                    <ul className="space-y-2 ml-11">
                      {step.instructions.map((instruction, i) => (
                        <li key={i} className="text-slate-600 text-sm flex items-start gap-2">
                          <span className="text-sky-500 mt-1">•</span>
                          {instruction}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Signs and Tips */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Signs */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-sky-700 mb-4 flex items-center gap-2">
                  ✨ Signes que l'exercice fonctionne
                </h3>
                <ul className="space-y-2">
                  {rosenbergExercise.signs.map((sign, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <span className="text-sky-500 font-bold">✓</span>
                      {sign}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2">
                   Conseils importants
                </h3>
                <ul className="space-y-2">
                  {rosenbergExercise.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3 text-slate-600">
                      <span className="text-slate-500 font-bold">→</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Reference */}
          <div className="p-6 bg-white/60 backdrop-blur-md rounded-xl border border-white/70 shadow-lg">
            <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              📚 Référence
            </h4>
            <p className="text-slate-700 italic">
              {rosenbergExercise.reference.author}, <strong>&quot;{rosenbergExercise.reference.title}&quot;</strong>, 
              préfacé par {rosenbergExercise.reference.preface}, {rosenbergExercise.reference.year}, {rosenbergExercise.reference.publisher}.
            </p>
          </div>
        </div>
      </section>

      {/* Scientific References */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bases Scientifiques
          </h2>
          
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="prose prose-sm max-w-none text-slate-600">
                <p className="mb-4">
                  La recherche sur le nerf vague a explosé ces dernières décennies, révélant son rôle central 
                  dans la santé physique et mentale. Des études ont montré que :
                </p>
                <ul className="space-y-2 ml-6">
                  <li>La stimulation vagale transcutanée est approuvée par la FDA pour traiter l'épilepsie et la dépression résistante</li>
                  <li>La méditation de pleine conscience augmente mesurablemente le tonus vagal après 8 semaines de pratique</li>
                  <li>Les techniques de respiration lente améliorent la VFC en quelques minutes</li>
                  <li>L'exposition au froid stimule immédiatement le nerf vague (diving reflex)</li>
                  <li>Le yoga et les chants activent le nerf vague par stimulation mécanique et neuronale</li>
                </ul>
                <p className="mt-6 p-4 rounded-lg text-sm">
                  <strong>Note :</strong> La théorie polyvagale de Stephen Porges a révolutionné notre compréhension 
                  du nerf vague, montrant qu'il ne s'agit pas d'un simple système binaire mais d'un réseau complexe 
                  avec différents états d'activation liés à nos comportements sociaux et notre réponse au stress.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Commencez Votre Pratique Aujourd'hui
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Découvrez nos exercices guidés de respiration et de cohérence cardiaque 
            pour renforcer votre nerf vague naturellement
          </p>
          <Link
            to="/breathing-app"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 text-white rounded-full font-semibold hover:bg-cyan-600 transition-colors shadow-lg"
          >
            Accéder aux exercices
            <ChevronLeft size={20} className="rotate-180" />
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default NerfVaguePage;
